import React, { useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { meHandler } from "src/store/reducers/meSlice";
import setAuthDataInLocalStorage from "src/utils/auth/LocalStorage";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import USEREMAILREGISTERED from "src/graphql/mutation/auth/userEmailRegistered";
import useStyles from "./Styles";

interface Props {
  changeModalType: (a: string) => void;
}

const EmailRequiredModal: React.FC<Props> = ({ changeModalType }) => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { me }: any = useSelector((state: any) => state.me);

  const { agentId } = useActiveUserDetail();

  const [
    userEmailRegistered,
    {
      data: userEmailRegisteredResult,
      loading: userEmailRegisteredLoading,
      error: userEmailRegisteredError,
    },
  ] = useMutation(USEREMAILREGISTERED);

  const handleFormikSubmit = async (
    FormValues: { email: string },
    formikHelpers: any,
  ) => {
    try {
      const { email } = FormValues;

      // send token to backend
      userEmailRegistered({
        variables: {
          email,
          agentId,
        },
      });
    } catch (error) {
      formikHelpers.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("Enter a valid email address")
        .required("Email is required"),
    }),
    onSubmit: handleFormikSubmit,
  });

  useEffect(() => {
    if (
      userEmailRegisteredResult &&
      userEmailRegisteredResult.userEmailRegistered
    ) {
      const { success, result, error } =
        userEmailRegisteredResult.userEmailRegistered;
      const parsedResult = JSON.parse(result);
      if (success) {
        enqueueSnackbar(parsedResult.message, {
          variant: "success",
        });
        const updatedMe = {
          ...me,
          email: parsedResult.email,
        };
        setAuthDataInLocalStorage(updatedMe);
        dispatch(meHandler(updatedMe));
        changeModalType("verify");
      } else {
        enqueueSnackbar(error, {
          variant: "error",
        });
      }
    }
  }, [userEmailRegisteredResult]);

  useEffect(() => {
    if (userEmailRegisteredError) {
      enqueueSnackbar(userEmailRegisteredError.message, {
        variant: "error",
      });
    }
  }, [userEmailRegisteredError]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="textSecondary">
          We couldn’t fetch your email from Facebook. Please provide it below to
          continue creating your account.
        </Typography>

        <Box>
          <TextField
            label="Email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            fullWidth
          />
          <Button
            sx={{ mt: "30px" }}
            disabled={userEmailRegisteredLoading}
            type="submit"
            variant="contained"
            color="primary"
            className={classes.saveButton}
            endIcon={
              userEmailRegisteredLoading && (
                <CircularProgress size={15} sx={{ color: "white" }} />
              )
            }
          >
            Save Email
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default EmailRequiredModal;
